import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PublicLayout } from '../components/layout/PublicLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Package, Bus, MapPin, User, Phone, QrCode, 
  Printer, ArrowLeft, AlertCircle, Clock, DollarSign,
  FileText, CheckCircle, Zap, Settings, Layers,
  RefreshCw, ArrowRightLeft, CheckCircle2
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  formatLuggageEscPos, 
  formatLuggageTagEscPos, 
  formatQrCodeOnlyEscPos, 
  formatTicketInfoOnlyEscPos, 
  printBluetoothEscPos, 
  pairBluetoothPrinter, 
  sendBytesToBluetoothDevice, 
  printToDualBluetoothThermalPrinters,
  getDualPrinter1,
  setDualPrinter1,
  getDualPrinter2,
  setDualPrinter2,
  swapDualPrinters
} from '../lib/bluetoothPrinter';
import { Wifi } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { getSystemDetailsUrl } from '../lib/qrCode';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const LUGGAGE_POLICY = `🧳 Panashe Coaches – Unclaimed Luggage Policy

📍 1. Lost & Found Registration
Any luggage found unclaimed on Panashe Coaches buses, offices, or depots will be officially recorded and kept in the Lost & Found section.

⏳ 2. Storage Period
Unclaimed luggage will be securely stored for a maximum period of three (3) months from the date it is reported or found.

🪪 3. Collection Procedure
To claim uncollected luggage, the owner must present:
• A valid national ID or passport
• A travel ticket or any acceptable proof of ownership

♻️ 4. Disposal of Unclaimed Luggage
If luggage remains unclaimed after three (3) months, Panashe Coaches reserves the right to:
• Donate the items to registered charities,
• Dispose of them in an environmentally responsible manner, or
• Sell them to recover storage and handling costs.

⚠️ 5. Liability
While reasonable care will be taken, Panashe Coaches shall not be held liable for loss, damage, or deterioration of unclaimed luggage during the storage period.

🚫 6. Prohibited & Perishable Items
Dangerous, illegal, or perishable items will not be stored and may be immediately disposed of or handed over to the relevant authorities.`;

const LuggageTicketPage = () => {
  const { luggageRef } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const printRef = useRef(null);
  
  const [luggage, setLuggage] = useState(null);
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [printing, setPrinting] = useState(false);
  const [btPrinting, setBtPrinting] = useState(false);
  const [includeTagSheet, setIncludeTagSheet] = useState(false);
  const [dualPrintOpen, setDualPrintOpen] = useState(false);
  const [dualMode, setDualMode] = useState(() => localStorage.getItem('panashe_dual_printer_mode') || 'bt_bt');
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [printer1Device, setPrinter1Device] = useState(() => getDualPrinter1());
  const [printer2Device, setPrinter2Device] = useState(() => getDualPrinter2());
  const [pairingP1, setPairingP1] = useState(false);
  const [pairingP2, setPairingP2] = useState(false);

  useEffect(() => {
    fetchLuggage();
  }, [luggageRef]);

  const fetchLuggage = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/luggage/${luggageRef}`);
      setLuggage(response.data);
      if (response.data?.trip_id) {
        try {
          const tripRes = await axios.get(`${API_URL}/trips/${response.data.trip_id}`);
          setTrip(tripRes.data);
        } catch (_) {}
      }
    } catch (error) {
      console.error('Failed to fetch luggage:', error);
      toast.error('Failed to load luggage details');
    } finally {
      setLoading(false);
    }
  };

  const canUserPrint = () => {
    if (!user) return true;
    if (user.role === 'conductor') {
      const assigned = trip?.conductor_id === user.id || luggage?.conductor_id === user.id;
      if (!assigned) {
        toast.error('You are not assigned as host/conductor to this trip and cannot print luggage receipts for it.');
        return false;
      }
    } else if (user.role === 'driver') {
      const assigned = trip?.driver_id === user.id || luggage?.driver_id === user.id;
      if (!assigned) {
        toast.error('You are not assigned as driver to this trip and cannot print luggage receipts for it.');
        return false;
      }
    }
    return true;
  };

  const recordPrint = async ({ printCount = 1, printType = 'ticket' } = {}) => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(
        `${API_URL}/print-history/record`,
        { document_type: 'luggage', reference: luggageRef, print_count: printCount, print_type: printType },
        { headers }
      );
    } catch (e) {
      console.warn('Failed to record print history:', e);
    }
  };

  const handleBluetoothPrint = async () => {
    if (!luggage) {
      toast.error('Luggage details are loading.');
      return;
    }
    if (!canUserPrint()) return;
    setBtPrinting(true);
    try {
      const bytes = formatLuggageEscPos(luggage);
      await printBluetoothEscPos(bytes);
      await recordPrint();
      toast.success('Luggage ticket sent to Bluetooth Thermal Printer & print logged!');
    } catch (err) {
      toast.error(err.message || 'Bluetooth printing failed.');
    } finally {
      setBtPrinting(false);
    }
  };

  const handlePrint = async () => {
    if (!canUserPrint()) return;
    setPrinting(true);
    await recordPrint({ printCount: 1, printType: 'ticket' });
    setTimeout(() => {
      window.print();
      setPrinting(false);
    }, 100);
  };

  const handlePrintWithTag = async () => {
    if (!canUserPrint()) return;
    setPrinting(true);
    setIncludeTagSheet(true);
    await recordPrint({ printCount: 2, printType: 'ticket_and_tag' });
    setTimeout(() => {
      window.print();
      setTimeout(() => setIncludeTagSheet(false), 400);
      setPrinting(false);
    }, 150);
  };

  const handlePairPrinter1 = async () => {
    setPairingP1(true);
    try {
      const dev = await pairBluetoothPrinter();
      setDualPrinter1(dev);
      setPrinter1Device(dev);
      toast.success(`✅ Printer 1 Connected: ${dev.name || 'Bluetooth Printer'} (Ticket Info)`);
    } catch (err) {
      if (err.name !== 'NotFoundError') {
        toast.error(`Printer 1 pairing error: ${err.message}`);
      }
    } finally {
      setPairingP1(false);
    }
  };

  const handlePairPrinter2 = async () => {
    setPairingP2(true);
    try {
      const dev = await pairBluetoothPrinter();
      setDualPrinter2(dev);
      setPrinter2Device(dev);
      toast.success(`✅ Printer 2 Connected: ${dev.name || 'Bluetooth Printer'} (QR Code Only)`);
    } catch (err) {
      if (err.name !== 'NotFoundError') {
        toast.error(`Printer 2 pairing error: ${err.message}`);
      }
    } finally {
      setPairingP2(false);
    }
  };

  const handleSwapPrinters = () => {
    const swapped = swapDualPrinters();
    setPrinter1Device(swapped.printer1);
    setPrinter2Device(swapped.printer2);
    toast.info('⇄ Printers swapped! Printer 1 & Printer 2 switched roles.');
  };

  const handleTestPrinter1 = async () => {
    if (!printer1Device) {
      toast.error('Please connect Printer 1 first.');
      return;
    }
    if (!luggage) return;
    try {
      toast.loading('Sending test ticket to Printer 1...', { id: 'test-p1' });
      const bytes = formatTicketInfoOnlyEscPos(luggage);
      await sendBytesToBluetoothDevice(printer1Device, bytes);
      toast.success('✅ Printer 1 test ticket info printed!', { id: 'test-p1' });
    } catch (e) {
      toast.error(`Printer 1 test failed: ${e.message}`, { id: 'test-p1' });
    }
  };

  const handleTestPrinter2 = async () => {
    if (!printer2Device) {
      toast.error('Please connect Printer 2 first.');
      return;
    }
    if (!luggage) return;
    try {
      toast.loading('Sending test QR Code tag to Printer 2...', { id: 'test-p2' });
      const bytes = formatQrCodeOnlyEscPos(luggage, window.location.origin);
      await sendBytesToBluetoothDevice(printer2Device, bytes);
      toast.success('✅ Printer 2 test QR Code tag printed!', { id: 'test-p2' });
    } catch (e) {
      toast.error(`Printer 2 test failed: ${e.message}`, { id: 'test-p2' });
    }
  };

  // Dedicated QR-Code-Only Thermal Print Popup (for Standard / USB thermal printers)
  const openQrOnlyPrintPopup = () => {
    if (!luggage) return false;
    const qrSvgElement = document.querySelector('.tag-qr svg') || document.querySelector('.qr-box svg');
    const qrSvgHtml = qrSvgElement ? qrSvgElement.outerHTML : '';
    const qrWindow = window.open('', '_blank', 'width=380,height=560');
    if (!qrWindow) {
      toast.error('Pop-up was blocked. Please allow popups in your browser to print to 2 printers simultaneously.');
      return false;
    }

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Luggage QR Code - ${luggage.luggage_ref}</title>
          <style>
            @page { size: auto; margin: 2mm; }
            body { 
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; 
              margin: 0; padding: 6px; background: white; text-align: center; color: #000;
            }
            .qr-tag {
              border: 2.5px solid #000;
              border-radius: 8px;
              padding: 10px;
              max-width: 320px;
              margin: 0 auto;
              box-sizing: border-box;
            }
            .header { font-size: 14px; font-weight: 900; letter-spacing: 1px; }
            .ref { font-size: 20px; font-weight: 900; color: #b91c1c; margin: 2px 0; }
            .tag-num { font-size: 11px; font-weight: 700; color: #475569; }
            .dest-badge {
              font-size: 16px;
              font-weight: 900;
              color: #000;
              border: 2px solid #000;
              padding: 4px 6px;
              margin: 6px 0;
              border-radius: 4px;
              text-transform: uppercase;
            }
            .route { font-size: 10px; font-weight: 700; color: #1e3a8a; margin-bottom: 6px; }
            .qr { display: flex; justify-content: center; margin: 6px 0; }
            .qr svg { width: 220px !important; height: 220px !important; }
            .details {
              font-size: 10px;
              color: #000;
              text-align: left;
              border-top: 1px solid #000;
              padding-top: 5px;
              line-height: 1.4;
            }
            @media print {
              body { padding: 0; }
              .qr-tag { border: 2.5px solid #000; }
            }
          </style>
        </head>
        <body>
          <div class="qr-tag">
            <div class="header">PANASHE COACHES</div>
            <div class="ref">${luggage.luggage_ref}</div>
            <div class="tag-num">Tag #${luggage.luggage_number || luggage.luggage_ref}</div>
            <div class="dest-badge">DEST: ${luggage.custom_destination || luggage.destination || 'N/A'}</div>
            <div class="route">${luggage.origin} &rarr; ${luggage.custom_destination || luggage.destination}</div>
            <div class="qr">${qrSvgHtml}</div>
            <div class="details">
              <div><strong>Item:</strong> ${luggage.description || 'Luggage'} (${luggage.quantity || 1} item)</div>
              <div><strong>Receiver:</strong> ${luggage.receiver_name || 'Self Collect'}</div>
              <div><strong>Sender:</strong> ${luggage.sender_name || 'N/A'} (${luggage.sender_phone || ''})</div>
              ${luggage.is_fragile ? '<div style="font-weight:bold;color:#b91c1c;">⚠️ FRAGILE - HANDLE WITH CARE</div>' : ''}
            </div>
          </div>
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                setTimeout(function() { window.close(); }, 800);
              }, 250);
            };
          </script>
        </body>
      </html>
    `;
    qrWindow.document.open();
    qrWindow.document.write(html);
    qrWindow.document.close();
    return true;
  };

  const handleSimultaneousDualPrint = async (mode = dualMode) => {
    if (!luggage) return;

    if (saveAsDefault) {
      localStorage.setItem('panashe_dual_printer_mode', mode);
    }

    if (mode === 'bt_bt') {
      // 1. Bluetooth Thermal (Ticket Info Only) + 2. Bluetooth Thermal (QR Code Only) SIMULTANEOUSLY
      if (!printer1Device || !printer2Device) {
        toast.error('Please connect both Printer 1 and Printer 2 using the pairing buttons before printing.');
        return;
      }

      recordPrint({ printCount: 2, printType: 'dual_bt_bt' }).catch(() => {});
      setBtPrinting(true);
      try {
        toast.loading('⚡ Printing to both thermal printers simultaneously...', { id: 'dual-bt' });
        await printToDualBluetoothThermalPrinters(printer1Device, printer2Device, luggage, window.location.origin);
        toast.success('🎉 Both printed at the same time! Printer 1: Ticket Info, Printer 2: QR Code Only', { id: 'dual-bt' });
        setDualPrintOpen(false);
      } catch (err) {
        console.error('Dual Bluetooth print error:', err);
        toast.error(`Bluetooth print error: ${err.message || 'Transmission failed'}`, { id: 'dual-bt' });
      } finally {
        setBtPrinting(false);
      }
    } else if (mode === 'standard_bt') {
      // Printer 1: Standard/USB (Ticket Info) + Printer 2: Bluetooth (QR Code Only)
      recordPrint({ printCount: 2, printType: 'dual_standard_bt' }).catch(() => {});
      setBtPrinting(true);
      try {
        const qrBytes = formatQrCodeOnlyEscPos(luggage, window.location.origin);
        if (printer2Device) {
          sendBytesToBluetoothDevice(printer2Device, qrBytes).catch(e => {
            console.error('Bluetooth QR send error:', e);
            toast.error(`Printer 2 (Bluetooth) error: ${e.message}`);
          });
        } else {
          await printBluetoothEscPos(qrBytes);
        }
        toast.success('✅ QR Tag sent to Bluetooth Thermal Printer!');

        // Concurrently trigger Standard/USB print for Ticket Information
        setPrinting(true);
        setIncludeTagSheet(false);
        setTimeout(() => {
          window.print();
          setPrinting(false);
        }, 100);
        setDualPrintOpen(false);
      } catch (err) {
        console.error('Standard + Bluetooth print error:', err);
        toast.error(`Printer error: ${err.message}`);
      } finally {
        setBtPrinting(false);
      }
    } else if (mode === 'standard_standard') {
      // 1. Standard/USB Printer 1 (Ticket Info) + 2. Standard/USB Printer 2 (QR Code Only)
      recordPrint({ printCount: 2, printType: 'dual_standard_standard' }).catch(() => {});
      
      // Fire QR Code Only popup print
      openQrOnlyPrintPopup();

      // Concurrently trigger Ticket Info print dialog
      setPrinting(true);
      setIncludeTagSheet(false);
      setTimeout(() => {
        window.print();
        setPrinting(false);
      }, 200);
      setDualPrintOpen(false);
    } else {
      // Single printer batch (both on 1 printer)
      handlePrintWithTag();
      setDualPrintOpen(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      registered: 'bg-blue-100 text-blue-700',
      in_transit: 'bg-yellow-100 text-yellow-700',
      at_destination: 'bg-green-100 text-green-700',
      delivered: 'bg-emerald-100 text-emerald-700',
      past_destination: 'bg-red-100 text-red-700',
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="max-w-3xl mx-auto px-4 py-8">
          <Skeleton className="h-[600px] w-full" />
        </div>
      </PublicLayout>
    );
  }

  if (!luggage) {
    return (
      <PublicLayout>
        <div className="max-w-3xl mx-auto px-4 py-8 text-center">
          <AlertCircle className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <h2 className="font-heading text-2xl font-bold mb-2">Luggage Not Found</h2>
          <p className="text-slate-600 mb-4">Reference: {luggageRef}</p>
          <Button onClick={() => navigate(-1)} className="btn-panashe">
            Go Back
          </Button>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Action Buttons - Hidden on Print */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6 print:hidden">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          <div className="flex items-center gap-2">
            <Button
              onClick={handleBluetoothPrint}
              disabled={btPrinting}
              variant="outline"
              className="border-indigo-500 text-indigo-700 hover:bg-indigo-50 font-semibold"
              title="Connect & print directly to Bluetooth Thermal Printer (58mm/80mm)"
            >
              <Wifi className="h-4 w-4 mr-1.5 text-indigo-600" />
              {btPrinting ? 'Printing...' : '🛜 Bluetooth Thermal Print'}
            </Button>

            <Button onClick={handlePrint} className="btn-panashe" disabled={printing}>
              <Printer className="h-4 w-4 mr-2" />
              {printing ? 'Printing...' : 'Standard Print'}
            </Button>

            <Button
              onClick={() => setDualPrintOpen(true)}
              disabled={printing || btPrinting}
              className="bg-amber-600 hover:bg-amber-500 text-white font-semibold shadow-sm"
              title="Print Ticket & Luggage QR Tag simultaneously across 2 printers"
            >
              <Zap className="h-4 w-4 mr-1.5 text-yellow-300" />
              Print Ticket + QR Tag (2 Printers)
            </Button>
          </div>
        </div>

        {/* Dual Simultaneous Printers Dialog - Compact & Responsive */}
        <Dialog open={dualPrintOpen} onOpenChange={setDualPrintOpen}>
          <DialogContent className="max-w-md w-full bg-white p-5 rounded-2xl shadow-2xl max-h-[88vh] overflow-y-auto">
            <DialogHeader className="pb-2 border-b">
              <DialogTitle className="flex items-center gap-2 text-base font-bold text-slate-900">
                <Zap className="h-5 w-5 text-amber-500" />
                Simultaneous 2-Printer Dispatcher
              </DialogTitle>
              <DialogDescription className="text-slate-500 text-xs">
                Printer 1: <strong>Ticket Info</strong> &bull; Printer 2: <strong>QR Code Only</strong>
              </DialogDescription>
            </DialogHeader>

            {/* Compact Mode Segment Switcher */}
            <div className="flex bg-slate-100 p-1 rounded-lg gap-1 my-2.5 text-xs font-semibold">
              <button
                type="button"
                onClick={() => setDualMode('bt_bt')}
                className={`flex-1 py-1 px-2 rounded-md transition-all text-center ${
                  dualMode === 'bt_bt'
                    ? 'bg-white text-indigo-700 shadow-sm font-bold'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                🛜 Dual Bluetooth
              </button>
              <button
                type="button"
                onClick={() => setDualMode('standard_standard')}
                className={`flex-1 py-1 px-2 rounded-md transition-all text-center ${
                  dualMode === 'standard_standard'
                    ? 'bg-white text-amber-700 shadow-sm font-bold'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                🖨️ Dual USB
              </button>
              <button
                type="button"
                onClick={() => setDualMode('standard_bt')}
                className={`flex-1 py-1 px-2 rounded-md transition-all text-center ${
                  dualMode === 'standard_bt'
                    ? 'bg-white text-blue-700 shadow-sm font-bold'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                ⚡ Hybrid
              </button>
            </div>

            {/* Mode 1: Dual Bluetooth Thermal Printers */}
            {dualMode === 'bt_bt' && (
              <div className="space-y-2.5">
                {printer1Device && printer2Device && (
                  <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs px-2.5 py-1.5 rounded-lg flex items-center justify-between font-medium">
                    <span className="flex items-center gap-1.5">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                      Both thermal printers connected &amp; ready!
                    </span>
                    <button
                      type="button"
                      onClick={handleSwapPrinters}
                      className="text-[11px] text-emerald-900 hover:underline flex items-center gap-1 font-bold"
                      title="Swap Printer 1 and 2 roles"
                    >
                      <ArrowRightLeft className="h-3 w-3" /> Swap
                    </button>
                  </div>
                )}

                <div className="border rounded-xl p-2.5 bg-slate-50/70 space-y-2">
                  {/* Printer 1: Ticket Info Only */}
                  <div className="flex items-center justify-between gap-2 p-2 bg-white rounded-lg border border-slate-200">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-bold text-xs text-slate-800">Printer 1 (Ticket Info):</span>
                        {printer1Device ? (
                          <Badge className="bg-emerald-100 text-emerald-800 text-[10px] py-0 px-1.5">Connected</Badge>
                        ) : (
                          <Badge variant="outline" className="text-slate-500 text-[10px] py-0 px-1.5">Offline</Badge>
                        )}
                      </div>
                      <p className="text-[11px] text-slate-600 truncate mt-0.5">
                        {printer1Device ? `Device: ${printer1Device.name || 'Printer 1'}` : 'Passenger & trip details'}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {printer1Device && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={handleTestPrinter1} 
                          className="h-7 text-xs px-2 text-indigo-700 hover:bg-indigo-50"
                          title="Test ticket print"
                        >
                          Test
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant={printer1Device ? "outline" : "default"}
                        onClick={handlePairPrinter1}
                        disabled={pairingP1}
                        className={`h-7 text-xs px-2.5 ${!printer1Device ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : ''}`}
                      >
                        {pairingP1 ? 'Pairing...' : printer1Device ? 'Change' : '🔗 Connect'}
                      </Button>
                    </div>
                  </div>

                  {/* Printer 2: QR Code Only */}
                  <div className="flex items-center justify-between gap-2 p-2 bg-white rounded-lg border border-slate-200">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="font-bold text-xs text-slate-800">Printer 2 (QR Code):</span>
                        {printer2Device ? (
                          <Badge className="bg-emerald-100 text-emerald-800 text-[10px] py-0 px-1.5">Connected</Badge>
                        ) : (
                          <Badge variant="outline" className="text-slate-500 text-[10px] py-0 px-1.5">Offline</Badge>
                        )}
                      </div>
                      <p className="text-[11px] text-slate-600 truncate mt-0.5">
                        {printer2Device ? `Device: ${printer2Device.name || 'Printer 2'}` : 'Dedicated QR code parcel sticker'}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {printer2Device && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={handleTestPrinter2} 
                          className="h-7 text-xs px-2 text-purple-700 hover:bg-purple-50"
                          title="Test QR tag print"
                        >
                          Test
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant={printer2Device ? "outline" : "default"}
                        onClick={handlePairPrinter2}
                        disabled={pairingP2}
                        className={`h-7 text-xs px-2.5 ${!printer2Device ? 'bg-purple-600 hover:bg-purple-700 text-white' : ''}`}
                      >
                        {pairingP2 ? 'Pairing...' : printer2Device ? 'Change' : '🔗 Connect'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Mode 2: Dual USB */}
            {dualMode === 'standard_standard' && (
              <div className="p-3 bg-amber-50/70 border border-amber-200 rounded-xl text-xs text-slate-700 space-y-1">
                <p className="font-bold text-amber-900">🖨️ Dual USB / Windows Thermal Drivers</p>
                <p>&bull; <strong>Printer 1:</strong> Passenger Ticket prints via primary print spooler.</p>
                <p>&bull; <strong>Printer 2:</strong> Luggage QR Code Tag prints via instant popup window.</p>
                <p className="text-amber-800 font-semibold pt-1">⚡ Both print streams trigger simultaneously.</p>
              </div>
            )}

            {/* Mode 3: Hybrid */}
            {dualMode === 'standard_bt' && (
              <div className="p-3 bg-blue-50/70 border border-blue-200 rounded-xl space-y-2 text-xs">
                <div>
                  <p className="font-bold text-blue-900">⚡ Standard (Ticket) + 🛜 Bluetooth (QR Tag)</p>
                  <p className="text-slate-600">&bull; Printer 1 (Standard): Passenger Luggage Ticket</p>
                  <p className="text-slate-600">&bull; Printer 2 (Bluetooth): Luggage QR Code Tag</p>
                </div>
                <div className="flex items-center justify-between p-2 bg-white rounded-lg border border-blue-200">
                  <div className="text-[11px] text-slate-600">
                    <span className="font-bold text-slate-800">Printer 2 (BT):</span>{' '}
                    {printer2Device ? printer2Device.name || 'Connected' : 'Not paired'}
                  </div>
                  <Button
                    size="sm"
                    variant={printer2Device ? "outline" : "default"}
                    onClick={handlePairPrinter2}
                    disabled={pairingP2}
                    className="h-7 text-xs"
                  >
                    {printer2Device ? 'Change' : '🔗 Connect BT'}
                  </Button>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between pt-3 mt-1 border-t">
              <label className="flex items-center gap-1.5 text-xs text-slate-500 cursor-pointer">
                <input
                  type="checkbox"
                  checked={saveAsDefault}
                  onChange={(e) => setSaveAsDefault(e.target.checked)}
                  className="rounded text-indigo-600 h-3.5 w-3.5"
                />
                Remember mode
              </label>

              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={() => setDualPrintOpen(false)} className="h-9 text-xs">
                  Cancel
                </Button>
                <Button 
                  onClick={() => handleSimultaneousDualPrint(dualMode)} 
                  className="bg-panashe-red hover:bg-red-700 text-white font-bold h-9 px-4 text-xs shadow-md"
                  disabled={printing || btPrinting}
                >
                  <Zap className="h-4 w-4 mr-1 text-yellow-300" />
                  Print Both Simultaneously
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Printable Ticket */}
        <div ref={printRef} className="luggage-container">
          <Card className="card-panashe overflow-hidden print:shadow-none print:border-2 print:border-black">
            {/* Header: 3-column — Logo | Company+Ref | QR Code */}
            <div className="bg-gradient-to-r from-panashe-blue to-red-900 p-5 text-white print:bg-white print:text-black print:border-b-2 print:border-black">
              <div className="header-row flex items-center justify-between gap-3">
                {/* Left: Logo */}
                <div className="logo-box bg-white p-1.5 border-2 border-white/30 rounded-lg flex-shrink-0 shadow-sm print:border-slate-300">
                  <img 
                    src="/panashe-logo.jpg" 
                    alt="Panashe Coaches" 
                    className="logo rounded object-cover"
                    style={{ width: 80, height: 80, display: 'block' }}
                  />
                </div>

                {/* Center: Company name + luggage ref + status */}
                <div className="header-center flex-1 text-center">
                  <h1 className="company-name font-heading text-2xl font-bold tracking-wide print:text-xl">PANASHE COACHES</h1>
                  <p className="ticket-type text-white/80 print:text-slate-600 text-sm mt-0.5">Luggage Ticket</p>
                  <p className="booking-ref font-heading text-2xl font-bold mt-2 print:text-xl">{luggage.luggage_ref}</p>
                  <Badge className={`${getStatusColor(luggage.status)} mt-1.5`}>
                    {luggage.status?.replace('_', ' ').toUpperCase()}
                  </Badge>
                </div>

                {/* Right: QR Code */}
                <div className="flex-shrink-0 text-center">
                  <div className="qr-box bg-white p-1.5 border-2 border-white/30 rounded-lg shadow-sm print:border-slate-300">
                    <QRCodeSVG 
                      value={getSystemDetailsUrl(luggage.luggage_ref, 'luggage')} 
                      size={144}
                      level="H"
                      includeMargin
                    />
                  </div>
                  <p className="scan-label text-xs text-white/70 print:text-slate-500 mt-1">Scan to verify</p>
                </div>
              </div>
            </div>

            <CardContent className="p-4 space-y-3">
              {/* ── ROW 1: Departure & Item Bar ── */}
              <div className="flex items-center justify-between bg-panashe-red/10 px-4 py-2.5 rounded-lg text-sm">
                <div>
                  <p className="text-slate-500 text-xs">Luggage Number</p>
                  <p className="font-bold text-lg text-panashe-red leading-tight">{luggage.luggage_number || luggage.luggage_ref}</p>
                </div>
                <div className="text-center flex-1 px-4">
                  <p className="text-slate-500 text-xs">Departure Date &amp; Time</p>
                  <p className="font-bold text-panashe-red">{luggage.departure_datetime ? format(parseISO(luggage.departure_datetime), 'EEE, dd MMM yyyy @ HH:mm') : 'N/A'}</p>
                </div>
                <div className="text-right">
                  <p className="text-slate-500 text-xs">Quantity &amp; Type</p>
                  <p className="font-medium text-xs">{luggage.quantity || 1} item(s) • <span className="capitalize">{luggage.item_type || 'General'}</span></p>
                </div>
              </div>

              {/* ── ROW 2: Trip Details + Sender/Receiver side by side ── */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                {/* LEFT: Trip Details */}
                <div className="border rounded-lg p-3">
                  <h3 className="font-heading font-bold text-slate-900 mb-2 flex items-center gap-1.5 text-sm border-b pb-1.5">
                    <Bus className="h-4 w-4 flex-shrink-0" />
                    Trip Details
                  </h3>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex justify-between gap-2">
                      <span className="text-slate-500 flex-shrink-0">Origin</span>
                      <span className="font-bold text-blue-700 text-right">{luggage.origin}</span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-slate-500 flex-shrink-0">Destination</span>
                      <span className="font-bold text-green-700 text-right">{luggage.custom_destination || luggage.destination}</span>
                    </div>
                    <div className="flex justify-between gap-2">
                      <span className="text-slate-500 flex-shrink-0">Bus</span>
                      <span className="font-medium text-right">{luggage.bus_registration || 'N/A'}</span>
                    </div>
                    {luggage.passenger_ticket_number && (
                      <div className="flex justify-between gap-2">
                        <span className="text-slate-500 flex-shrink-0">Passenger ticket</span>
                        <span className="font-mono text-right">{luggage.passenger_ticket_number}</span>
                      </div>
                    )}
                    {luggage.passenger_name && (
                      <div className="flex justify-between gap-2">
                        <span className="text-slate-500 flex-shrink-0">Passenger</span>
                        <span className="font-medium text-right">{luggage.passenger_name}</span>
                      </div>
                    )}
                    <div className="flex justify-between gap-2">
                      <span className="text-slate-500 flex-shrink-0">Route</span>
                      <span className="font-medium text-right">{luggage.route_name || `${luggage.origin} → ${luggage.destination}`}</span>
                    </div>
                  </div>
                </div>

                {/* RIGHT: Sender & Receiver */}
                <div className="grid grid-rows-2 gap-2 text-xs">
                  <div className="bg-blue-50 p-2.5 rounded-lg border border-blue-200">
                    <p className="font-bold text-blue-900 flex items-center gap-1">
                      <User className="h-3 w-3" /> Sender: {luggage.sender_name}
                    </p>
                    <p className="text-slate-600 flex items-center gap-1 mt-0.5">
                      <Phone className="h-3 w-3" /> {luggage.sender_phone} {luggage.sender_id_number && `• ID: ${luggage.sender_id_number}`}
                    </p>
                  </div>
                  <div className="bg-green-50 p-2.5 rounded-lg border border-green-200">
                    <p className="font-bold text-green-900 flex items-center gap-1">
                      <MapPin className="h-3 w-3" /> Receiver: {luggage.receiver_name || 'Self Collect'}
                    </p>
                    {luggage.receiver_phone && (
                      <p className="text-slate-600 flex items-center gap-1 mt-0.5">
                        <Phone className="h-3 w-3" /> {luggage.receiver_phone}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {luggage.collected_by_name && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs">
                  <p className="font-bold text-emerald-900">Collection recorded</p>
                  <p className="text-emerald-800 mt-1">
                    Collected by {luggage.collected_by_name}
                    {luggage.collected_by_id_number ? ` · ID/Passport ${luggage.collected_by_id_number}` : ''}
                    {luggage.collected_at ? ` · ${new Date(luggage.collected_at).toLocaleString()}` : ''}
                  </p>
                  {luggage.collected_by_staff_name && <p className="text-slate-600 mt-1">Released by {luggage.collected_by_staff_name}</p>}
                </div>
              )}

              {/* ── ROW 3: Description & Payment side by side ── */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                {/* LEFT: Description */}
                <div className="bg-slate-50 p-3 rounded-lg border text-xs">
                  <h4 className="font-bold text-slate-900 mb-1">Description &amp; Notes</h4>
                  <p className="text-slate-700">{luggage.description}</p>
                  {luggage.declared_value > 0 && (
                    <p className="text-slate-700 mt-1 font-medium">
                      Declared Value: <span className="font-bold text-slate-900">{luggage.declared_value_currency === 'USD' ? '$' : 'R'}{luggage.declared_value?.toFixed(2)} ({luggage.declared_value_currency || 'ZAR'})</span>
                    </p>
                  )}
                  {luggage.special_notes && <p className="text-slate-500 mt-1">Notes: {luggage.special_notes}</p>}
                  {luggage.is_fragile && <Badge className="bg-yellow-100 text-yellow-800 mt-1">⚠️ FRAGILE</Badge>}
                </div>

                {/* RIGHT: Payment Details */}
                <div className="border rounded-lg p-3 text-xs">
                  <h3 className="font-heading font-bold text-slate-900 mb-1.5 flex items-center gap-1.5 border-b pb-1">
                    <DollarSign className="h-3.5 w-3.5" /> Payment Details
                  </h3>
                  <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                    <div>
                      <span className="text-slate-500">Charge</span>
                      <p className="font-medium">{luggage.currency || '$'}{luggage.total_charge?.toFixed(2) || '0.00'}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Paid</span>
                      <p className="font-bold text-green-600">{luggage.currency || '$'}{luggage.amount_paid?.toFixed(2) || '0.00'}</p>
                    </div>
                  </div>
                  {luggage.balance_cleared ? (
                    <div className="mt-1.5 bg-green-50 p-2 rounded border border-green-400">
                      <p className="text-green-800 font-bold">✓ PAY-FORWARD PAID IN FULL</p>
                      <p className="text-green-700 text-[11px] mt-0.5">
                        Received at: <span className="font-semibold">{luggage.balance_cleared_station || 'SA Station'}</span> by <span className="font-semibold">{luggage.balance_cleared_by_name || 'Staff'}</span>
                      </p>
                      <p className="text-green-900 font-bold text-[11px] mt-0.5">
                        ✅ CLEARED FOR RELEASE TO RECEIVER
                      </p>
                    </div>
                  ) : luggage.is_pay_forward && luggage.pay_forward_balance > 0 ? (
                    <div className="mt-1.5 bg-orange-50 p-2 rounded border border-orange-300">
                      <p className="text-orange-700 font-bold">💰 Balance Due: {luggage.currency === 'ZAR' ? 'R' : '$'}{luggage.pay_forward_balance?.toFixed(2)}</p>
                      <p className="text-red-700 font-extrabold text-[11px] mt-0.5">
                        🚫 NOT CLEARED: Must be paid before handover!
                      </p>
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Collection Notice */}
              <div className="bg-yellow-50 p-2.5 rounded-lg border border-yellow-400 text-xs">
                <h4 className="font-bold text-yellow-800 mb-0.5">⚠️ Requirements:</h4>
                <p className="text-yellow-700">Bring this ticket + ID/Passport to collect luggage. Receiver name must match ticket.</p>
              </div>

              {/* Policy & Footer */}
              <div className="border-t pt-2 text-[8px] text-slate-500 leading-tight">
                <p className="font-bold">TERMS &amp; CONDITIONS</p>
                <p className="line-clamp-2">{LUGGAGE_POLICY}</p>
              </div>

              <div className="border-t pt-2 flex justify-between items-center text-xs text-slate-500">
                <div>
                  <p>Contact: +263786563430 | +263777275154</p>
                </div>
                <div className="text-right">
                  <p>Generated: {format(new Date(), 'dd MMM yyyy HH:mm')} | <span className="font-mono">{luggage.luggage_ref}</span></p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {includeTagSheet && (
          <div className="luggage-tag-sheet print-only">
            <div className="tag-block">
              <div className="tag-header">PANASHE COACHES</div>
              <div className="tag-ref">{luggage.luggage_ref}</div>
              <div className="tag-qr">
                <QRCodeSVG value={getSystemDetailsUrl(luggage.luggage_ref, 'luggage')} size={300} level="H" includeMargin />
              </div>
              <div className="tag-meta">{luggage.description}</div>
              <div className="tag-meta">Destination: {luggage.custom_destination || luggage.destination}</div>
              <div className="tag-meta">Sender: {luggage.sender_name}</div>
              <div className="tag-meta">Receiver: {luggage.receiver_name || 'Self collect'}</div>
            </div>
          </div>
        )}

        {/* Responsive Print Styles */}
        <style>{`
          .luggage-tag-sheet {
            display: none;
          }
          .tag-block {
            width: 340px;
            min-height: 400px;
            border: 2px solid #0f172a;
            border-radius: 12px;
            padding: 12px;
            text-align: center;
            background: white;
            margin: 12px auto;
            page-break-inside: avoid;
            box-sizing: border-box;
          }
          .tag-header {
            font-size: 14px;
            font-weight: 800;
            letter-spacing: 0.08em;
            color: #0f172a;
          }
          .tag-ref {
            margin-top: 6px;
            font-size: 18px;
            font-weight: 700;
            color: #b91c1c;
          }
          .tag-qr {
            display: flex;
            justify-content: center;
            margin: 10px 0;
          }
          .tag-qr svg {
            width: 300px;
            height: 300px;
          }
          .tag-meta {
            font-size: 10px;
            color: #334155;
            line-height: 1.5;
            word-break: break-word;
          }
          @media print {
            @page { size: auto; margin: 6mm; }
            html, body { background: #ffffff !important; margin: 0 !important; padding: 0 !important; }
            nav, header, footer, .print\\:hidden, [data-testid="back-btn"], [data-testid="print-btn"] { display: none !important; }
            body * { visibility: hidden; }
            .luggage-container, .luggage-container *, .luggage-tag-sheet, .luggage-tag-sheet * { visibility: visible; }
            .luggage-container {
              position: absolute;
              left: 0; top: 0;
              width: 100% !important;
              max-width: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
              box-shadow: none !important;
            }
            .luggage-tag-sheet {
              display: block !important;
              position: relative;
              margin-top: 18px;
              page-break-before: always;
            }
            .card-panashe { box-shadow: none !important; border: 1px solid #cbd5e1 !important; }
            /* Force 2-column side-by-side grid in print out */
            .grid-cols-2 { display: grid !important; grid-template-columns: repeat(2, minmax(0, 1fr)) !important; gap: 0.75rem !important; }
            .flex { display: flex !important; }
            .justify-between { justify-content: space-between !important; }
            /* Logo & QR size */
            .logo { width: 75px !important; height: 75px !important; }
            .qr-box svg { width: 1.4in !important; height: 1.4in !important; }
            .tag-qr svg { width: 3.125in !important; height: 3.125in !important; }
            /* Fluid layout */
            .header-row { display: flex !important; justify-content: space-between !important; align-items: center !important; gap: 8px !important; }
            .header-center { flex: 1 !important; text-align: center !important; }
          }
        `}</style>
      </div>
    </PublicLayout>
  );
};

export default LuggageTicketPage;
